import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import api from '@/api/client'
import type { User } from '@/types/models'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(localStorage.getItem('token'))
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const userRole = computed(() => user.value?.role || null)
  const isSuperAdmin = computed(() => user.value?.role === 'super_admin')
  const isTenantUser = computed(() => user.value?.tenant_id !== null)
  // Roles locked to the standalone /operator panel — no admin SPA access.
  const isPanelOnly = computed(
    () => user.value?.role === 'operator_panel' || user.value?.role === 'instructor',
  )
  // Customers have no business in the admin SPA at all (they use the widget
  // cabinet). Used to hard-block them from every admin route.
  const isCustomer = computed(() => user.value?.role === 'customer')

  function can(permission: string): boolean {
    if (!user.value) return false
    const perms = user.value.permissions || []
    return perms.includes('*') || perms.includes(permission)
  }

  /**
   * Tenant-level switches, delivered with the session because operators need
   * some of them (the payment-modal document warning) while having no rights
   * to read /admin/settings.
   */
  const features = computed(() => user.value?.tenant_features ?? {})
  const documentsEnabled = computed(() => features.value.documents_enabled === true)
  // Defaults to true: an absent flag means the park has not configured it, and
  // warning is the chosen default for the payment screen.
  const documentsPaymentWarning = computed(() => features.value.documents_payment_warning !== false)
  // Defaults to true, same reasoning: an unconfigured park keeps today's
  // behaviour rather than losing the QR button it never chose to hide.
  const documentsQrUploadEnabled = computed(() => features.value.documents_qr_upload_enabled !== false)

  // Actions
  async function login(phone: string, password: string) {
    isLoading.value = true
    error.value = null
    
    try {
      const response = await api.post('/auth/login', { phone, password })
      token.value = response.data.token
      user.value = response.data.user
      
      localStorage.setItem('token', response.data.token)
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`

      // /auth/login runs outside tenant context (it resolves the user by host
      // itself), so its payload cannot carry tenant-scoped flags. /auth/me does.
      // Without this the first session after login would behave as if every
      // tenant feature were switched off until the next page load.
      await fetchUser()

      return true
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Ошибка авторизации'
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function logout() {
    try {
      await api.post('/auth/logout')
    } finally {
      token.value = null
      user.value = null
      localStorage.removeItem('token')
      delete api.defaults.headers.common['Authorization']
    }
  }

  async function fetchUser() {
    if (!token.value) return

    try {
      api.defaults.headers.common['Authorization'] = `Bearer ${token.value}`
      const response = await api.get('/auth/me')
      user.value = response.data
    } catch (err: any) {
      // Only an actual rejection of the token ends the session. Anything else —
      // a 404 from tenant resolution, a 500, the network being out — leaves the
      // session alone: it may still be perfectly valid, and tearing it down here
      // presents as a mystery. That is exactly what happened to super admins,
      // whose /auth/me 404s on the central host: login() called this, it logged
      // them straight back out, and the sidebar rendered empty because `user`
      // was null while the router had already let them through.
      if (err?.response?.status === 401) {
        logout()
      }
    }
  }

  // Handle impersonation token from URL
  const urlParams = new URLSearchParams(window.location.search)
  const urlToken = urlParams.get('token')
  if (urlToken) {
    token.value = urlToken
    localStorage.setItem('token', urlToken)
    // Clean URL
    const url = new URL(window.location.href)
    url.searchParams.delete('token')
    url.searchParams.delete('impersonate')
    window.history.replaceState({}, '', url.pathname + url.search)
  }

  // Hydrate the user from the persisted token. Cached as a promise so the
  // router guard can `await authStore.init()` without firing /me twice.
  // Without this, F5 would set token from localStorage but isAuthenticated
  // (which requires user too) was still false when the guard ran → redirect
  // to /login even though the session was valid.
  let initPromise: Promise<void> | null = null
  function init(): Promise<void> {
    if (initPromise) return initPromise
    initPromise = token.value ? fetchUser() : Promise.resolve()
    return initPromise
  }

  return {
    user,
    token,
    isLoading,
    error,
    isAuthenticated,
    userRole,
    isSuperAdmin,
    isTenantUser,
    isPanelOnly,
    isCustomer,
    can,
    features,
    documentsEnabled,
    documentsPaymentWarning,
    documentsQrUploadEnabled,
    login,
    logout,
    fetchUser,
    init,
  }
})
